import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCheck,
  ExternalLink,
  FileText,
  Megaphone,
  MessageSquare,
  Paperclip,
  Radio,
  RefreshCw,
  Signal,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
  type RadarComunicacao,
  type RadarSeveridade,
  type RadarTipo,
  useMarcarComunicacoesLidas,
  useRadarTimeline,
} from '@/hooks/useRadar';
import { useRadarStream } from '@/hooks/useRadarStream';
import { ItensDoProcesso } from './ItensDoProcesso';
import { chaveDoDia, formatarDataHora, formatarDiaLegivel, formatarHora, paraData } from './radar-format';

const TIPOS: Array<{ value: RadarTipo | ''; label: string }> = [
  { value: '', label: 'Todos os eventos' },
  { value: 'convocacao', label: 'Convocações e prazos' },
  { value: 'mudanca_status', label: 'Mudanças de status' },
  { value: 'documento', label: 'Documentos' },
  { value: 'mensagem', label: 'Mensagens' },
  { value: 'outro', label: 'Outros eventos' },
];

const tipoLabel: Record<RadarTipo, string> = {
  convocacao: 'Convocação',
  mudanca_status: 'Status',
  documento: 'Documento',
  mensagem: 'Mensagem',
  outro: 'Evento',
};

const tipoIcone: Record<RadarTipo, typeof MessageSquare> = {
  convocacao: Megaphone,
  mudanca_status: Signal,
  documento: FileText,
  mensagem: MessageSquare,
  outro: Radio,
};

const severidadeLabel: Record<RadarSeveridade, string> = {
  informativa: 'Informativa',
  atencao: 'Atenção',
  acao_necessaria: 'Ação necessária',
  critica: 'Crítica',
};

const severidadeLinha: Record<RadarSeveridade, string> = {
  informativa: 'border-ds-border bg-ds-surface2',
  atencao: 'border-ds-info/40 bg-ds-info/5',
  acao_necessaria: 'border-ds-warning/50 bg-ds-warning/10',
  critica: 'border-ds-danger/60 bg-ds-danger/10',
};

const severidadeSelo: Record<RadarSeveridade, string> = {
  informativa: 'bg-ds-surface text-ds-text-secondary',
  atencao: 'bg-ds-info/15 text-ds-info',
  acao_necessaria: 'bg-ds-warning/20 text-ds-warning',
  critica: 'bg-ds-danger/20 text-ds-danger',
};

const estadoLabel: Record<string, string> = {
  nao_configurado: 'não configurado',
  em_descoberta: 'em descoberta',
  validacao_tecnica: 'em validação técnica',
  validacao_juridica: 'em validação jurídica',
  piloto: 'em piloto',
  ativo: 'ativo',
  degradado: 'com falha na coleta',
  pausado: 'pausado',
  revogado: 'revogado',
};

export interface AndamentoDoProcessoProps {
  /** `empresa_licitacoes_pipeline.id` da licitação acompanhada. */
  pipelineId: number;
  /** Visão da equipe (`/empresas/:id/...`): troca o cliente e o endpoint. */
  empresaId?: number;
  /** Desliga as ações de escrita (marcar lido). Obrigatório na visão da equipe. */
  somenteLeitura?: boolean;
  /** Link do portal oficial quando a licitação não tem monitoramento. */
  fallbackPortalUrl?: string | null;
  /** Fallback de polling do React Query enquanto o SSE está fora. */
  intervaloPollingMs?: number;
  /** O SSE existe só no portal da empresa. */
  tempoReal?: boolean;
  id?: string;
}

const LIMITE_PAGINA = 50;

function RotuloEvidencia() {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-ds-text-secondary"
      title="O conteúdo original desta comunicação ficou arquivado como evidência."
    >
      <Paperclip className="h-3 w-3" aria-hidden="true" />
      Evidência arquivada
    </span>
  );
}

function LinhaComunicacao({ item, portalUrl }: { item: RadarComunicacao; portalUrl: string | null }) {
  const quando = item.publicado_em || item.observado_em;
  const data = paraData(quando);
  const Icone = tipoIcone[item.tipo] || Radio;
  const estilo = item.severidade ? severidadeLinha[item.severidade] : 'border-ds-border bg-ds-surface';

  return (
    <li className={`rounded-lg border p-3 ${estilo}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <time
          dateTime={data ? data.toISOString() : undefined}
          className="font-medium tabular-nums text-ds-text-primary"
        >
          {formatarHora(quando)}
        </time>
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-surface2 px-2 py-0.5 font-medium text-ds-text-secondary">
          <Icone className="h-3 w-3" aria-hidden="true" />
          {tipoLabel[item.tipo] || 'Evento'}
        </span>
        {item.severidade && (
          <span className={`rounded-full px-2 py-0.5 font-medium ${severidadeSelo[item.severidade]}`}>
            {severidadeLabel[item.severidade]}
          </span>
        )}
        {!item.lido && <span className="font-medium text-ds-info">Não lida</span>}
      </div>

      <p className="mt-2 text-sm font-medium text-ds-text-primary">{item.autor || 'Portal'}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-ds-text-secondary">{item.conteudo}</p>

      {item.acao_recomendada && (
        <p className="mt-2 text-sm text-ds-text-primary">
          <strong className="font-medium">O que fazer:</strong> {item.acao_recomendada}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-3">
        {item.evidencia_referencia && <RotuloEvidencia />}
        {item.tipo === 'documento' && portalUrl && (
          <a
            href={portalUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-ds-info hover:underline focus:outline-none focus:ring-2 focus:ring-ds-brand"
          >
            Conferir documento no portal oficial
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
      </div>
    </li>
  );
}

/**
 * Andamento do processo: as comunicações que o Radar coletou do portal oficial.
 *
 * A nossa cópia nunca substitui a fonte — o link do portal fica sempre à mão e,
 * quando a licitação não tem sessão monitorada, a tela diz isso em vez de
 * mostrar uma timeline vazia que parece bug.
 */
export function AndamentoDoProcesso({
  pipelineId,
  empresaId,
  somenteLeitura = false,
  fallbackPortalUrl = null,
  intervaloPollingMs = 5_000,
  tempoReal = true,
  id = 'andamento-do-processo',
}: AndamentoDoProcessoProps) {
  const [tipo, setTipo] = useState<RadarTipo | ''>('');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [page, setPage] = useState(1);

  const timeline = useRadarTimeline({
    pipelineId,
    empresaId,
    tipo,
    busca: buscaAplicada,
    page,
    limit: LIMITE_PAGINA,
    intervaloMs: intervaloPollingMs,
  });

  const marcarLidas = useMarcarComunicacoesLidas(pipelineId);
  const jaMarcou = useRef<number | null>(null);

  useRadarStream({ pipelineId, habilitado: tempoReal && !empresaId });

  const dados = timeline.data;
  const sessao = dados?.sessao || null;
  const itens = useMemo(() => dados?.itens || [], [dados]);
  const naoLidas = itens.filter((item) => !item.lido).length;

  // Abrir o andamento marca as comunicações daquela licitação como lidas.
  useEffect(() => {
    if (somenteLeitura || !sessao || jaMarcou.current === pipelineId) return;
    if (!itens.some((item) => !item.lido)) return;
    jaMarcou.current = pipelineId;
    marcarLidas.mutate(undefined);
  }, [itens, marcarLidas, pipelineId, sessao, somenteLeitura]);

  const grupos = useMemo(() => {
    const mapa = new Map<string, { chave: string; referencia: string | null; itens: RadarComunicacao[] }>();
    for (const item of itens) {
      const quando = item.publicado_em || item.observado_em;
      const chave = chaveDoDia(quando);
      const grupo = mapa.get(chave);
      if (grupo) grupo.itens.push(item);
      else mapa.set(chave, { chave, referencia: quando || null, itens: [item] });
    }
    return Array.from(mapa.values());
  }, [itens]);

  const estado = sessao?.estado || '';
  const estadoIntegracao = sessao?.estadoIntegracao || '';
  const degradado = estado === 'degradado' || estadoIntegracao === 'degradado';
  const paginacao = dados?.pagination;
  const totalPaginas = Math.max(paginacao?.totalPages || 1, 1);
  const verificacao = paraData(sessao?.ultimaVerificacaoEm);

  return (
    <Card id={id} className="p-5" aria-labelledby={`${id}-titulo`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <Radio className="mt-1 h-5 w-5 shrink-0 text-ds-brand" aria-hidden="true" />
          <div>
            <p className="text-xs uppercase tracking-wide text-ds-text-secondary">Radar</p>
            <h2 id={`${id}-titulo`} className="text-lg font-semibold text-ds-text-primary">
              Andamento do processo
            </h2>
            <p className="mt-1 text-sm text-ds-text-secondary">
              {somenteLeitura
                ? 'Visão de leitura das comunicações coletadas do portal oficial.'
                : 'Comunicações preservadas do portal oficial, com a data em que foram observadas.'}
            </p>
          </div>
        </div>
        {!somenteLeitura && sessao && (
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            onClick={() => marcarLidas.mutate(undefined)}
            disabled={marcarLidas.isPending || naoLidas === 0}
            aria-label={
              naoLidas > 0
                ? `Marcar ${naoLidas} comunicações como lidas`
                : 'Todas as comunicações já estão marcadas como lidas'
            }
          >
            <CheckCheck className="h-4 w-4" aria-hidden="true" />
            {naoLidas > 0 ? `Marcar ${naoLidas} como lidas` : 'Tudo lido'}
          </Button>
        )}
      </div>

      {timeline.isLoading && (
        <p role="status" className="mt-4 text-sm text-ds-text-secondary">
          Carregando o andamento do processo...
        </p>
      )}

      {dados?.semMonitoramento && (
        <div role="status" className="mt-4 rounded-lg border border-ds-warning/30 bg-ds-warning/10 p-4">
          <p className="font-medium text-ds-text-primary">Monitoramento ainda não conectado</p>
          <p className="mt-1 text-sm text-ds-text-secondary">
            Esta licitação ainda não tem uma sessão monitorada. Consulte o portal oficial até o Radar ser ativado.
          </p>
          {fallbackPortalUrl && (
            <a
              href={fallbackPortalUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-ds-info hover:underline focus:outline-none focus:ring-2 focus:ring-ds-brand"
            >
              Abrir portal oficial
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          )}
        </div>
      )}

      {timeline.isError && (
        <div role="alert" className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-ds-danger/10 p-4 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-ds-danger" aria-hidden="true" />
          <span className="text-ds-danger">Não foi possível carregar o andamento agora.</span>
          <Button size="sm" variant="secondary" className="gap-2" onClick={() => void timeline.refetch()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      )}

      {sessao && (
        <div className="mt-5 space-y-5">
          <div className="rounded-lg bg-ds-surface2 p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-ds-text-primary">
                <strong>{sessao.portalNome || sessao.portal}</strong> • monitoramento{' '}
                {estadoLabel[estado] || estado || 'não informado'}
              </span>
              {sessao.urlOrigem && (
                <a
                  href={sessao.urlOrigem}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-ds-info hover:underline focus:outline-none focus:ring-2 focus:ring-ds-brand"
                >
                  Abrir portal oficial
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
            </div>
            <p className="mt-1 text-xs text-ds-text-secondary">
              Última verificação:{' '}
              {verificacao ? (
                <time dateTime={verificacao.toISOString()}>{formatarDataHora(sessao.ultimaVerificacaoEm)}</time>
              ) : (
                'não informada'
              )}
            </p>
            {typeof sessao.participantesContagem === 'number' && sessao.participantesContagem > 0 && (
              <p
                className="mt-1 text-xs text-ds-text-secondary"
                title="Fornecedores distintos observados nesta sessão. Número parcial e anonimizado — só da fase de lances deste portal; a ausência não significa que ninguém se cadastrou."
              >
                <span className="inline-flex items-center gap-1 rounded-full border border-ds-border px-2 py-0.5 text-ds-text-primary">
                  {sessao.participantesContagem} na disputa
                </span>
              </p>
            )}
          </div>

          {degradado && (
            <div role="alert" className="rounded-lg border border-ds-danger/40 bg-ds-danger/10 p-4">
              <p className="flex items-center gap-2 font-medium text-ds-text-primary">
                <AlertTriangle className="h-4 w-4 shrink-0 text-ds-danger" aria-hidden="true" />
                A coleta deste processo está com problema
              </p>
              <p className="mt-1 text-sm text-ds-text-secondary">
                Pode haver comunicação do portal que ainda não chegou aqui. Confirme o andamento no portal oficial.
              </p>
              <p className="mt-2 text-xs text-ds-text-secondary">
                Última verificação bem-sucedida:{' '}
                {verificacao ? (
                  <time dateTime={verificacao.toISOString()}>{formatarDataHora(sessao.ultimaVerificacaoEm)}</time>
                ) : (
                  'não informada'
                )}
              </p>
              {sessao.erroSanitizado && (
                <p className="mt-1 text-xs text-ds-danger">Motivo informado pela coleta: {sessao.erroSanitizado}</p>
              )}
            </div>
          )}

          <ItensDoProcesso sessao={sessao} id={`${id}-itens`} />

          <section aria-labelledby={`${id}-linha-do-tempo`}>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 id={`${id}-linha-do-tempo`} className="font-medium text-ds-text-primary">
                  Linha do tempo
                </h3>
                <p className="mt-1 text-sm text-ds-text-secondary" role="status">
                  {paginacao?.total || 0} comunicações importadas
                  {naoLidas > 0 ? ` • ${naoLidas} não lidas nesta página` : ''}.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2" role="group" aria-label="Filtrar o andamento">
                <label className="text-xs text-ds-text-secondary" htmlFor={`${id}-tipo`}>
                  <span className="mb-1 block">Tipo</span>
                  <select
                    id={`${id}-tipo`}
                    value={tipo}
                    onChange={(evento) => {
                      setTipo(evento.target.value as RadarTipo | '');
                      setPage(1);
                    }}
                    className="rounded-lg border border-ds-border bg-ds-surface px-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-brand"
                  >
                    {TIPOS.map((opcao) => (
                      <option key={opcao.value} value={opcao.value}>
                        {opcao.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-ds-text-secondary" htmlFor={`${id}-busca`}>
                  <span className="mb-1 block">Buscar</span>
                  <input
                    id={`${id}-busca`}
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                    onKeyDown={(evento) => {
                      if (evento.key !== 'Enter') return;
                      setBuscaAplicada(busca);
                      setPage(1);
                    }}
                    placeholder="Autor ou texto"
                    className="rounded-lg border border-ds-border bg-ds-surface px-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-brand"
                  />
                </label>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setBuscaAplicada(busca);
                    setPage(1);
                  }}
                >
                  Buscar
                </Button>
              </div>
            </div>

            {grupos.length > 0 ? (
              <ol className="mt-4 space-y-5">
                {grupos.map((grupo) => (
                  <li key={grupo.chave}>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-ds-text-secondary">
                      {grupo.referencia ? (
                        <time dateTime={grupo.chave}>{formatarDiaLegivel(grupo.referencia)}</time>
                      ) : (
                        'Data não informada'
                      )}
                    </h4>
                    <ul className="mt-2 space-y-2" aria-label={`Comunicações de ${formatarDiaLegivel(grupo.referencia)}`}>
                      {grupo.itens.map((item) => (
                        <LinhaComunicacao key={item.id} item={item} portalUrl={sessao.urlOrigem} />
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            ) : (
              !timeline.isLoading && (
                <p role="status" className="mt-4 rounded-lg border border-dashed border-ds-border p-4 text-sm text-ds-text-secondary">
                  {tipo || buscaAplicada
                    ? 'Nenhuma comunicação encontrada com esse filtro.'
                    : 'O monitoramento está conectado, mas ainda não importou comunicações deste processo.'}
                </p>
              )
            )}

            {totalPaginas > 1 && (
              <div className="mt-4 flex flex-wrap items-center gap-3" role="group" aria-label="Paginação do andamento">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage((atual) => Math.max(atual - 1, 1))}
                  disabled={page <= 1}
                  aria-label="Comunicações mais recentes"
                >
                  Mais recentes
                </Button>
                <span className="text-sm text-ds-text-secondary" role="status">
                  Página {page} de {totalPaginas}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage((atual) => Math.min(atual + 1, totalPaginas))}
                  disabled={page >= totalPaginas}
                  aria-label="Comunicações anteriores"
                >
                  Carregar mais antigas
                </Button>
              </div>
            )}
          </section>
        </div>
      )}
    </Card>
  );
}
